import { useSelector } from 'react-redux';
import { psychologistSelectorById } from '../../redux/specialists/selector';
import { selectConsultationsBySpecialistAndClient } from '../../redux/consultations/selector';
import { isClientCan as isClientCanUtil, Specialist, ClientCanAction, Session } from '@mindly/ui-components';
import { useCallback } from 'react';
import { userSelector } from 'src/redux/user/selector';
import store from 'src/redux';

type IsClientCanParams = {
  action: ClientCanAction;
  specialistId?: Specialist['id'];
};

export const useIsClientCan = (specialistIdParam: Specialist['id'] = '') => {
  const currentUser = useSelector(userSelector);
  const specialistData = useSelector(psychologistSelectorById(specialistIdParam));
  const consultationsData = useSelector(
    selectConsultationsBySpecialistAndClient(specialistIdParam, currentUser?.id ?? '')
  );

  const getSpecialist = useCallback(
    (id?: Specialist['id']) => {
      if (id) {
        const state = store.getState();
        return psychologistSelectorById(id)(state) as unknown as Specialist;
      }

      return specialistData as unknown as Specialist;
    },
    [specialistData]
  );

  const getConsultations = useCallback(
    (specialistId?: Specialist['id']) => {
      if (specialistId) {
        const state = store.getState();
        return selectConsultationsBySpecialistAndClient(
          specialistId,
          currentUser?.id ?? ''
        )(state) as unknown as Session[];
      }
      return consultationsData as unknown as Session[];
    },
    [consultationsData, currentUser?.id]
  );

  const isClientCan = useCallback(
    ({ action, specialistId = specialistIdParam ?? '' }: IsClientCanParams) => {
      const specialist = getSpecialist(specialistId);
      const sessions = getConsultations(specialistId);

      if (!specialist) {
        return false;
      }

      return isClientCanUtil({
        action,
        specialist,
        sessions,
      });
    },
    [getSpecialist, getConsultations]
  );

  return {
    isClientCan,
  };
};
